import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import {
  completeProfileFormSchema,
  getSupportedTimeZones,
  profileFormSchema,
  type OnboardingWorkingHours,
  type ProfileForm,
  type ProfileResponse,
} from './onboarding-profile-contracts'
import {
  onboardingProfileQueryOptions,
  onboardingStatusQueryOptions,
} from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './ProfilePage.module.css'

type ClinicField = keyof ProfileForm['clinicInfo']
type DayKey = keyof OnboardingWorkingHours
type DayDraft = { open: boolean; start: string; end: string }
type HoursDraft = Record<DayKey, DayDraft>
type FormErrors = Record<string, string>

const DAYS: Array<{ key: DayKey; label: string }> = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
]

const CLINIC_FIELDS: Array<{
  key: ClinicField
  label: string
  type?: 'email' | 'tel'
  wide?: boolean
}> = [
  { key: 'dentistryName', label: 'Clinic name' },
  { key: 'dentistryPhone', label: 'Clinic phone', type: 'tel' },
  { key: 'dentistryEmail', label: 'Clinic email', type: 'email' },
  { key: 'emergencyPhone', label: 'Emergency phone (optional)', type: 'tel' },
  { key: 'dentistryAddress', label: 'Clinic address', wide: true },
]

function defaultHours(): HoursDraft {
  return Object.fromEntries(
    DAYS.map(({ key }) => [
      key,
      {
        open: !['sat', 'sun'].includes(key),
        start: '09:00',
        end: '17:00',
      },
    ])
  ) as HoursDraft
}

function hoursFromContract(hours?: OnboardingWorkingHours): HoursDraft {
  if (!hours) return defaultHours()
  return Object.fromEntries(
    DAYS.map(({ key }) => {
      const interval = hours[key]
      const closed = interval === '00:00-00:00'
      return [
        key,
        {
          open: !closed,
          start: closed ? '09:00' : interval.slice(0, 5),
          end: closed ? '17:00' : interval.slice(6),
        },
      ]
    })
  ) as HoursDraft
}

function hoursToContract(hours: HoursDraft): OnboardingWorkingHours {
  return Object.fromEntries(
    DAYS.map(({ key }) => [
      key,
      hours[key].open ? `${hours[key].start}-${hours[key].end}` : '00:00-00:00',
    ])
  ) as OnboardingWorkingHours
}

function formFromResponse(
  response: ProfileResponse,
  ownerEmail: string | null
): { form: ProfileForm; hours: HoursDraft } {
  const timeZones = getSupportedTimeZones()
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const fallbackTimeZone = timeZones.includes(browserTimeZone)
    ? browserTimeZone
    : 'UTC'
  return {
    form: {
      clinicInfo: {
        dentistryName: response.profileDraft.clinicInfo?.dentistryName ?? '',
        dentistryAddress:
          response.profileDraft.clinicInfo?.dentistryAddress ?? '',
        dentistryPhone: response.profileDraft.clinicInfo?.dentistryPhone ?? '',
        dentistryEmail:
          response.profileDraft.clinicInfo?.dentistryEmail ?? ownerEmail ?? '',
        emergencyPhone: response.profileDraft.clinicInfo?.emergencyPhone ?? '',
      },
      timeZone: response.profileDraft.timeZone ?? fallbackTimeZone,
      workingHours:
        response.profileDraft.workingHours ?? hoursToContract(defaultHours()),
    },
    hours: hoursFromContract(response.profileDraft.workingHours),
  }
}

function errorsFromValidation(
  validation: ReturnType<typeof profileFormSchema.safeParse>
): FormErrors {
  if (validation.success) return {}
  const errors: FormErrors = {}
  for (const issue of validation.error.issues) {
    errors[issue.path.join('.')] ??= issue.message
  }
  return errors
}

export function ProfilePage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return (
    <AuthenticatedProfilePage
      key={auth.user.uid}
      client={client}
      user={auth.user}
    />
  )
}

function AuthenticatedProfilePage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const statusQuery = useQuery(onboardingStatusQueryOptions(client, user))

  if (statusQuery.isPending || statusQuery.isFetching) {
    return <Spinner label="Checking your setup" />
  }
  if (statusQuery.isError) {
    if (
      statusQuery.error instanceof OnboardingApiError &&
      statusQuery.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <div className={styles.page}>
        <StatePanel
          kind="error"
          title="Clinic setup unavailable"
          description="Retry this check. If it continues to fail, contact Flio support."
        />
        <div className={styles.actions}>
          <Button type="button" onClick={() => void statusQuery.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  const destination = routeOnboardingResponse(statusQuery.data)
  if (destination !== 'profile') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  return <ProfileEditor client={client} user={user} onSignOut={auth.logout} />
}

function ProfileEditor({
  client,
  user,
  onSignOut,
}: {
  client: OnboardingApiClient
  user: DashboardUser
  onSignOut: () => Promise<void>
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const profileQuery = useQuery(onboardingProfileQueryOptions(client, user))
  const submissionStarted = useRef(false)
  const [baseline, setBaseline] = useState<ProfileForm | null>(null)
  const [draft, setDraft] = useState<ProfileForm | null>(null)
  const [hours, setHours] = useState<HoursDraft | null>(null)
  const [revision, setRevision] = useState<number | null>(null)
  const [working, setWorking] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [feedback, setFeedback] = useState<
    'saved' | 'conflict' | 'error' | null
  >(null)
  const timeZones = useMemo(() => getSupportedTimeZones(), [])

  const applyResponse = useCallback(
    (response: ProfileResponse) => {
      const loaded = formFromResponse(response, user.email)
      setBaseline(loaded.form)
      setDraft(loaded.form)
      setHours(loaded.hours)
      setRevision(response.revision)
      setErrors({})
    },
    [user.email]
  )

  useEffect(() => {
    if (profileQuery.data && !baseline) applyResponse(profileQuery.data)
  }, [applyResponse, baseline, profileQuery.data])

  if (profileQuery.isError) {
    if (
      profileQuery.error instanceof OnboardingApiError &&
      profileQuery.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <div className={styles.page}>
        <StatePanel
          kind={
            profileQuery.error instanceof OnboardingApiError &&
            profileQuery.error.code === 'forbidden'
              ? 'forbidden'
              : 'error'
          }
          title="Clinic profile unavailable"
          description="This profile could not be loaded safely. Retry or contact Flio support."
        />
        <div className={styles.actions}>
          <Button type="button" onClick={() => void profileQuery.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }
  if (
    profileQuery.isPending ||
    profileQuery.isFetching ||
    !draft ||
    !hours ||
    revision === null
  ) {
    return <Spinner label="Loading clinic profile" />
  }

  const current: ProfileForm = {
    ...draft,
    workingHours: hoursToContract(hours),
  }
  const activeProfileRevision = revision
  const isDirty = JSON.stringify(current) !== JSON.stringify(baseline)

  function updateClinicField(field: ClinicField, value: string) {
    if (working) return
    setDraft((existing) =>
      existing
        ? {
            ...existing,
            clinicInfo: { ...existing.clinicInfo, [field]: value },
          }
        : existing
    )
    setFeedback(null)
    setErrors((existing) => {
      const next = { ...existing }
      delete next[`clinicInfo.${field}`]
      return next
    })
  }

  function updateDay(day: DayKey, update: Partial<DayDraft>) {
    if (working) return
    setHours((existing) =>
      existing
        ? { ...existing, [day]: { ...existing[day], ...update } }
        : existing
    )
    setFeedback(null)
    setErrors((existing) => {
      const next = { ...existing }
      delete next[`workingHours.${day}`]
      return next
    })
  }

  function handleFailure(error: unknown) {
    if (
      error instanceof OnboardingApiError &&
      error.code === 'revision-conflict'
    ) {
      setFeedback('conflict')
    } else {
      setFeedback('error')
    }
  }

  async function save() {
    if (submissionStarted.current) return
    const validation = profileFormSchema.safeParse(current)
    if (!validation.success) {
      setErrors(errorsFromValidation(validation))
      return
    }
    submissionStarted.current = true
    setWorking(true)
    setFeedback(null)
    try {
      const saved = await client.saveProfile(
        user,
        activeProfileRevision,
        validation.data
      )
      queryClient.setQueryData(
        onboardingProfileQueryOptions(client, user).queryKey,
        saved
      )
      applyResponse(saved)
      setFeedback('saved')
    } catch (error) {
      handleFailure(error)
    } finally {
      submissionStarted.current = false
      setWorking(false)
    }
  }

  async function complete() {
    if (submissionStarted.current) return
    const validation = completeProfileFormSchema.safeParse(current)
    if (!validation.success) {
      setErrors(errorsFromValidation(validation))
      return
    }
    submissionStarted.current = true
    setWorking(true)
    setFeedback(null)
    try {
      let activeRevision = activeProfileRevision
      if (isDirty) {
        const saved = await client.saveProfile(
          user,
          activeRevision,
          validation.data
        )
        queryClient.setQueryData(
          onboardingProfileQueryOptions(client, user).queryKey,
          saved
        )
        applyResponse(saved)
        activeRevision = saved.revision
      }
      const completed = await client.completeProfile(user, activeRevision)
      queryClient.setQueryData(
        onboardingProfileQueryOptions(client, user).queryKey,
        completed
      )
      await queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryOptions(client, user).queryKey,
        refetchType: 'none',
      })
      const status = await queryClient.fetchQuery(
        onboardingStatusQueryOptions(client, user)
      )
      void navigate(destinationPath(routeOnboardingResponse(status)), {
        replace: true,
      })
    } catch (error) {
      handleFailure(error)
    } finally {
      submissionStarted.current = false
      setWorking(false)
    }
  }

  async function reloadLatest() {
    setWorking(true)
    setFeedback(null)
    try {
      const result = await profileQuery.refetch()
      if (!result.data) throw new Error('Profile unavailable')
      applyResponse(result.data)
    } catch {
      setFeedback('error')
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Tell us about your clinic</h1>
        <p>
          Save a draft whenever you like. Continue only when the profile and
          weekly schedule are complete.
        </p>
      </header>
      <form
        className={styles.form}
        onSubmit={(event) => event.preventDefault()}
      >
        <Card className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Clinic information</h2>
            <p>Business contact details used during later setup.</p>
          </div>
          <fieldset className={styles.fields} disabled={working}>
            <legend className={styles.legend}>Clinic information</legend>
            {CLINIC_FIELDS.map(({ key, label, type, wide }) => {
              const error = errors[`clinicInfo.${key}`]
              return (
                <label
                  className={`${styles.field} ${wide ? styles.wide : ''}`}
                  key={key}
                >
                  <span>{label}</span>
                  <input
                    aria-invalid={Boolean(error)}
                    type={type ?? 'text'}
                    value={draft.clinicInfo[key]}
                    onChange={(event) =>
                      updateClinicField(key, event.currentTarget.value)
                    }
                  />
                  {error ? (
                    <small className={styles.error}>{error}</small>
                  ) : null}
                </label>
              )
            })}
          </fieldset>
        </Card>

        <Card className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Timezone</h2>
            <p>Choose the canonical IANA timezone used by your clinic.</p>
          </div>
          <label className={styles.field}>
            <span>Clinic timezone</span>
            <input
              list="onboarding-timezones"
              value={draft.timeZone}
              disabled={working}
              aria-invalid={Boolean(errors.timeZone)}
              onChange={(event) => {
                setDraft({ ...draft, timeZone: event.currentTarget.value })
                setFeedback(null)
                setErrors((existing) => {
                  const next = { ...existing }
                  delete next.timeZone
                  return next
                })
              }}
            />
            <datalist id="onboarding-timezones">
              {timeZones.map((timeZone) => (
                <option value={timeZone} key={timeZone} />
              ))}
            </datalist>
            {errors.timeZone ? (
              <small className={styles.error}>{errors.timeZone}</small>
            ) : null}
          </label>
        </Card>

        <Card className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Business hours</h2>
            <p>
              Times use the selected timezone. Overnight ranges are not
              supported in this release.
            </p>
          </div>
          <div className={styles.hours}>
            {DAYS.map(({ key, label }) => {
              const error = errors[`workingHours.${key}`]
              return (
                <fieldset
                  className={styles.hoursRow}
                  disabled={working}
                  key={key}
                >
                  <legend>{label}</legend>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={hours[key].open}
                      onChange={(event) =>
                        updateDay(key, { open: event.currentTarget.checked })
                      }
                    />
                    <span>{hours[key].open ? 'Open' : 'Closed'}</span>
                  </label>
                  <label className={styles.field}>
                    <span>From</span>
                    <input
                      aria-label={`${label} from`}
                      type="time"
                      value={hours[key].start}
                      disabled={working || !hours[key].open}
                      onChange={(event) =>
                        updateDay(key, { start: event.currentTarget.value })
                      }
                    />
                  </label>
                  <label className={styles.field}>
                    <span>To</span>
                    <input
                      aria-label={`${label} to`}
                      type="time"
                      value={hours[key].end}
                      disabled={working || !hours[key].open}
                      aria-invalid={Boolean(error)}
                      onChange={(event) =>
                        updateDay(key, { end: event.currentTarget.value })
                      }
                    />
                    {error ? (
                      <small className={styles.error}>{error}</small>
                    ) : null}
                  </label>
                </fieldset>
              )
            })}
          </div>
        </Card>

        {feedback ? (
          <p
            className={`${styles.feedback} ${feedback === 'error' ? styles.feedbackError : ''}`}
            role={feedback === 'error' ? 'alert' : 'status'}
          >
            {feedback === 'saved'
              ? 'Draft saved.'
              : feedback === 'conflict'
                ? 'This profile changed in another session. Your entries are preserved. Reload the latest version before saving again.'
                : 'The profile could not be saved. Your entries are still here; try again.'}
          </p>
        ) : null}

        <div className={styles.actions}>
          {feedback === 'conflict' ? (
            <Button
              type="button"
              variant="secondary"
              disabled={working}
              onClick={() => void reloadLatest()}
            >
              Reload latest
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            disabled={working}
            onClick={() => void onSignOut()}
          >
            Sign out
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={working || !isDirty}
            onClick={() => void save()}
          >
            {working ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            type="button"
            disabled={working}
            onClick={() => void complete()}
          >
            {working ? 'Working…' : 'Continue'}
          </Button>
        </div>
      </form>
    </div>
  )
}
